import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import { Input } from "@/components/ui/input"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { cn } from "@/lib/utils"
import { IconCalendar } from "@tabler/icons-react"
import { format, parse } from "date-fns"
import * as React from "react"

// A date field that displays/accepts typed input as dd/mm/yyyy AND provides a
// calendar popover for point-and-click selection. It stores and emits the value
// as an ISO yyyy-mm-dd string.
//
// Native <input type="date"> renders in the browser locale (not forceable to
// dd/mm/yyyy). This keeps typed text in local state and only commits a
// completed, valid date on blur or Enter, so intermediate keystrokes (e.g.
// typing the year) never trigger a navigation.

function isoToDisplay(iso?: string): string {
  if (!iso) return ""
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso)
  if (!m) return ""
  const [, y, mo, d] = m
  return `${d}/${mo}/${y}`
}

// Parse dd/mm/yyyy -> yyyy-mm-dd, returning undefined if incomplete/invalid.
function displayToIso(display: string): string | undefined {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(display)
  if (!m) return undefined
  const day = Number(m[1])
  const month = Number(m[2])
  const year = Number(m[3])
  if (month < 1 || month > 12) return undefined
  if (day < 1 || day > 31) return undefined
  // Validate the actual calendar date (rejects e.g. 31/02/2026).
  const date = new Date(year, month - 1, day)
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return undefined
  }
  return `${m[3]}-${m[2]}-${m[1]}`
}

// Keep only digits, cap at 8 (ddmmyyyy), and re-insert slashes as the user types.
function maskDisplay(raw: string): string {
  const digits = raw.replace(/\D/g, "").slice(0, 8)
  const parts = [digits.slice(0, 2), digits.slice(2, 4), digits.slice(4, 8)].filter((p) => p.length > 0)
  return parts.join("/")
}

interface DateInputProps extends Omit<React.ComponentProps<typeof Input>, "value" | "onChange" | "type"> {
  /** ISO yyyy-mm-dd value (or undefined when empty). */
  value?: string
  /** Called with an ISO yyyy-mm-dd string, or undefined when cleared. */
  onChange: (iso: string | undefined) => void
}

function DateInput({ value, onChange, onBlur, onKeyDown, className, ...props }: DateInputProps) {
  const [text, setText] = React.useState(() => isoToDisplay(value))
  const [open, setOpen] = React.useState(false)

  // Sync from external value changes (e.g. Clear filters) as long as the user
  // isn't mid-edit on this field.
  const isFocused = React.useRef(false)
  React.useEffect(() => {
    if (!isFocused.current) setText(isoToDisplay(value))
  }, [value])

  function commit(nextText: string) {
    if (nextText === "") {
      if (value) onChange(undefined)
      return
    }
    const iso = displayToIso(nextText)
    if (iso) {
      if (iso !== value) onChange(iso)
    } else {
      // Incomplete/invalid: revert to the last committed value.
      setText(isoToDisplay(value))
    }
  }

  const selected = value ? parse(value, "yyyy-MM-dd", new Date()) : undefined

  return (
    <div className={cn("relative", className)}>
      <Input
        inputMode="numeric"
        placeholder="dd/mm/yyyy"
        value={text}
        className="pr-9"
        onFocus={(e) => {
          isFocused.current = true
          // Select all so typing replaces the existing value cleanly instead of
          // interleaving new digits with the old ones.
          e.target.select()
        }}
        onChange={(e) => setText(maskDisplay(e.target.value))}
        onBlur={(e) => {
          isFocused.current = false
          commit(text)
          onBlur?.(e)
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit(text)
          onKeyDown?.(e)
        }}
        {...props}
      />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            tabIndex={-1}
            className="absolute right-0 top-0 h-9 w-9 text-muted-foreground hover:bg-transparent"
            aria-label="Open calendar"
          >
            <IconCalendar className="h-4 w-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="end">
          <Calendar
            mode="single"
            selected={selected}
            defaultMonth={selected}
            onSelect={(d) => {
              if (d) {
                const iso = format(d, "yyyy-MM-dd")
                setText(isoToDisplay(iso))
                if (iso !== value) onChange(iso)
              }
              setOpen(false)
            }}
            initialFocus
          />
        </PopoverContent>
      </Popover>
    </div>
  )
}

export { DateInput }
